import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckoutSecureNote } from "@/components/PaymentTrust";
import { Wallet, Check, ArrowRight, CheckCircle2, Zap, Loader2, Info, RefreshCw } from "lucide-react";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = RZP_SRC; s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const rcNum = (n) => `${Number(n || 0).toLocaleString("en-IN")} RC`;

// Fusion RC Recharge. All price / Base / Bonus / Total values are SERVER-AUTHORITATIVE
// (fetched from GET /billing/rc/packs and the create-order response). The client only ever
// sends pack_key; RC is credited exclusively after backend verify-payment succeeds.
export default function RcRechargeSection() {
  const [state, setState] = useState("loading"); // loading | disabled | ready | error
  const [packs, setPacks] = useState([]);
  const [validity, setValidity] = useState(12);
  const [sel, setSel] = useState(null);          // pack_key
  const [wallet, setWallet] = useState(null);
  const [paying, setPaying] = useState(false);    // create-order + gateway open
  const [verifying, setVerifying] = useState(false); // verify-payment running
  const [success, setSuccess] = useState(null);   // {base_rc, bonus_rc, total_rc_added, amount_paid_inr, idempotent}
  const busyRef = useRef(false);                   // hard guard against double-submit

  const loadPacks = () => {
    setState("loading");
    api.get("/billing/rc/packs")
      .then((r) => {
        if (!r.data?.enabled) { setState("disabled"); return; }
        setPacks(r.data.packs || []);
        setValidity(r.data.validity_months ?? 12);
        setState("ready");
      })
      .catch(() => setState("error"));
  };
  const loadWallet = () => {
    api.get("/connect/credits").then((r) => setWallet(r.data)).catch(() => {});
  };
  useEffect(() => { loadPacks(); loadWallet(); }, []);

  const pay = async () => {
    if (!sel || busyRef.current) return;
    busyRef.current = true;
    setPaying(true);
    setSuccess(null);
    try {
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) { toast.error("Failed to load payment gateway. Please retry."); return; }
      // Server-side order — only pack_key is sent; amount + RC are authoritative on the server.
      let data;
      try {
        ({ data } = await api.post("/billing/rc/create-order", { pack_key: sel }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not start recharge");
        return;
      }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: data.name || "RecoverOS", description: `Fusion RC Recharge — ${data.pack?.total_rc?.toLocaleString("en-IN")} RC`,
        prefill: data.prefill || {}, theme: { color: "#0B3D91" },
        modal: {
          // User closed the gateway without paying — no balance change.
          ondismiss: () => { if (!busyRef.current) return; busyRef.current = false; setPaying(false); toast.info("Recharge cancelled. No amount was charged."); },
        },
        handler: async (resp) => {
          // Payment authorized by Razorpay — RC is NOT granted yet. Verify on the server first.
          setVerifying(true);
          try {
            const { data: vr } = await api.post("/billing/rc/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setSuccess({
              base_rc: vr.base_rc, bonus_rc: vr.bonus_rc, total_rc_added: vr.total_rc_added,
              amount_paid_inr: vr.amount_paid_inr, idempotent: !!vr.idempotent,
            });
            setSel(null);
            if (vr.wallet) setWallet((w) => ({ ...(w || {}), wallet: vr.wallet }));
            loadWallet();
            window.dispatchEvent(new Event("recoveros:usage-changed"));
            if (vr.idempotent) toast.info("This payment was already processed — your balance is up to date.");
            else toast.success(`Recharge successful — ${vr.total_rc_added?.toLocaleString("en-IN")} RC added`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed. If money was debited it will be reconciled — do not retry immediately.");
          } finally {
            setVerifying(false);
            busyRef.current = false;
            setPaying(false);
          }
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. No amount was charged.");
        busyRef.current = false; setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start recharge");
      busyRef.current = false; setPaying(false);
    }
  };

  const busy = paying || verifying;
  const w = wallet?.wallet || {};

  return (
    <section id="recharge-rc" className="border border-border bg-card p-6 mb-5 scroll-mt-24" data-testid="rc-recharge-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-sm bg-[#16A34A]/10 flex items-center justify-center shrink-0">
            <Wallet className="h-6 w-6 text-[#16A34A]" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl">RecoverOS Credits — Fusion RC Recharge</div>
            <p className="text-sm text-muted-foreground mt-0.5">Top up Purchased RC on top of your Free Monthly RC. Bigger packs earn bonus RC.</p>
          </div>
        </div>
        {wallet && (
          <div className="text-right" data-testid="rc-wallet-balance">
            <div className="label-caps">Available Balance</div>
            <div className="text-2xl font-black font-heading mono mt-0.5">{rcNum(w.total_currently_available_rc ?? wallet.credit_balance_rc)}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Purchased: {rcNum(w.purchased_rc)} · Free this month: {rcNum(w.free_rc_available)}</div>
          </div>
        )}
      </div>

      {/* Info strip */}
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 rounded-sm bg-secondary/40 px-4 py-3 text-xs text-muted-foreground" data-testid="rc-info-strip">
        <span className="inline-flex items-center gap-1.5"><Info className="h-3.5 w-3.5 text-[#0B3D91]" /> <b className="text-foreground">1 RC = ₹1</b></span>
        <span className="inline-flex items-center gap-1.5"><Info className="h-3.5 w-3.5 text-[#0B3D91]" /> Purchased RC is valid for <b className="text-foreground">{validity} months</b></span>
        <span className="inline-flex items-center gap-1.5"><Info className="h-3.5 w-3.5 text-[#0B3D91]" /> Free Monthly RC is <b className="text-foreground">separate</b> from Purchased RC</span>
      </div>

      {/* Body states */}
      {state === "loading" && (
        <div className="flex items-center justify-center py-10" data-testid="rc-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      )}
      {state === "disabled" && (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="rc-disabled">RC recharge isn't available on your workspace yet.</div>
      )}
      {state === "error" && (
        <div className="py-8 text-center" data-testid="rc-error">
          <p className="text-sm text-muted-foreground">Could not load recharge packs.</p>
          <Button variant="outline" className="rounded-sm gap-1.5 mt-3" onClick={loadPacks} data-testid="rc-retry"><RefreshCw className="h-4 w-4" /> Retry</Button>
        </div>
      )}

      {state === "ready" && success && (
        <div className="mt-5 rounded-sm border border-[#16A34A]/30 bg-[#16A34A]/5 p-5 text-center" data-testid="rc-success">
          <div className="h-14 w-14 rounded-full bg-[#16A34A]/15 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-[#16A34A]" /></div>
          <h3 className="font-heading font-bold text-xl mt-3">{success.idempotent ? "Already credited" : "Recharge successful!"}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {inr(success.amount_paid_inr)} paid · <b className="text-foreground">{rcNum(success.total_rc_added)}</b> added
            {" "}({rcNum(success.base_rc)} base{success.bonus_rc > 0 ? <> + <b className="text-[#16A34A]">{rcNum(success.bonus_rc)} bonus</b></> : ""})
          </p>
          <Button variant="outline" className="rounded-sm mt-4" onClick={() => setSuccess(null)} data-testid="rc-buy-again">Buy more RC</Button>
        </div>
      )}

      {state === "ready" && !success && (
        <>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rc-packs">
            {packs.map((p) => {
              const active = sel === p.pack_key;
              return (
                <button type="button" key={p.pack_key} onClick={() => setSel(p.pack_key)} disabled={busy}
                  data-testid={`rc-pack-${p.pack_key}`}
                  className={`relative text-left rounded-sm border p-4 transition-colors disabled:opacity-60 ${active ? "border-[#16A34A] ring-1 ring-[#16A34A] bg-[#16A34A]/5" : "border-border hover:border-[#16A34A]/40"}`}>
                  {p.popular && <span className="absolute -top-2 right-3 bg-[#16A34A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Popular</span>}
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black font-heading">{inr(p.price_inr)}</span>
                    {active && <Check className="h-4 w-4 text-[#16A34A]" />}
                  </div>
                  <div className="mt-2 text-sm font-semibold" data-testid={`rc-pack-total-${p.pack_key}`}>{rcNum(p.total_rc)}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {rcNum(p.base_rc)} base{p.bonus_rc > 0 && <> + <span className="text-[#16A34A] font-semibold">{rcNum(p.bonus_rc)} bonus</span></>}
                  </div>
                </button>
              );
            })}
          </div>

          <Button onClick={pay} disabled={busy || !sel} className="mt-5 w-full sm:w-auto rounded-sm bg-[#16A34A] hover:bg-[#138a3e] gap-1.5" data-testid="rc-pay-btn">
            {verifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…</>
              : paying ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
              : <><Zap className="h-4 w-4" /> {sel ? `Recharge ${inr(packs.find((p) => p.pack_key === sel)?.price_inr)}` : "Select a pack"} <ArrowRight className="h-4 w-4" /></>}
          </Button>
          <CheckoutSecureNote className="mt-3" />
        </>
      )}
    </section>
  );
}
